import React from 'react';
import { Star } from '@material-ui/icons';
import { Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { Trans } from '@lingui/macro';
import { PermissionsEnum, SiteSummaryModel } from '@shared/models';
import { useSelectedSitesDataSource, useSitesDataSource } from '../sitesDataSource';
import { useSidebar } from '@shared/hooks';
import { useSiteListState } from '../useSiteListFiltersState';
import { useGraphDataSource } from '@features/DataView/Graph/graphDataSource';
import { isNil } from 'lodash';
import { usePermission, useFormatDate } from '@hwm/ui-lib';

export interface SiteListItemProps {
  siteData: SiteSummaryModel;
  height: number;
}

export const SiteListItem: React.FC<SiteListItemProps> = ({ siteData, height }) => {
  const theme = useTheme();
  const mediumScreen = useMediaQuery(theme.breakpoints.up('md'));

  const { hasPermission } = usePermission();
  const canSeeEclRsrpRsrqInfo = hasPermission(PermissionsEnum.CanSeeEclRsrpRsrqInfo);

  const selected = siteData?.isSelected;
  const { device } = siteData ?? {};

  const { handleSidebarState } = useSidebar();

  const { formatDate } = useFormatDate();

  const { handleFavorites } = useSitesDataSource();
  const { selectedSitesList, handleLoadSelectedList } = useSelectedSitesDataSource();

  const { setGraphSites } = useGraphDataSource();

  const { setSiteFilter } = useSiteListState();

  const onlyCSQData = !canSeeEclRsrpRsrqInfo || (isNil(device?.ecl) && isNil(device?.rsrp) && isNil(device?.rsrq));

  return siteData ? (
    <div style={{ height: '100%', minHeight: height }} className="flex flex-col mb-2">
      <div className="grid grid-cols-12 gap-1 sm:gap-2 items-center mb-1">
        <div className="flex flex-row items-center h-7 col-span-12 xl:col-span-4">
          <div
            className="flex cursor-pointer mr-2"
            onClick={() => handleFavorites(!selected, siteData?.id, false, setSiteFilter)}
          >
            {selected ? (
              <Star style={{ fill: 'gold', stroke: 'black' }} />
            ) : (
              <Star style={{ fill: 'transparent', stroke: 'black' }} />
            )}
          </div>
          <div
            className="flex justify-center items-center h-full bg-primaryColor px-2 py-1 md:py-2 text-white rounded-lg cursor-pointer transition hover:opacity-80"
            style={{ width: 'calc(100% - 32px)', minWidth: 'calc(100% - 32px)' }}
            onClick={() => {
              const addToFav = () =>
                handleFavorites(true, siteData?.id, selected, null, handleSidebarState, () => setGraphSites([]));
              handleLoadSelectedList(null, addToFav, !selectedSitesList);
            }}
          >
            <Typography style={{ fontWeight: 500 }} variant="body2" noWrap>
              {siteData?.siteID}
            </Typography>
          </div>
        </div>
        <div className="flex items-center h-7 bg-grey2 px-2 py-1 md:py-2 rounded-lg col-span-12 xl:col-span-8">
          <Typography variant="body2" noWrap>
            {siteData?.address ?? '-'}
          </Typography>
        </div>
      </div>
      <div className="flex flex-row flex-wrap">
        <Typography className="md:w-44" variant="body2" style={{ marginRight: 8 }}>
          <b>
            <Trans>Call In</Trans>
          </b>
          : {device?.lastCallIn ? formatDate(device?.lastCallIn, 'P HH:mm') : '-'}
        </Typography>
        <Typography className="md:w-40" variant="body2" style={{ marginRight: 8 }}>
          <b>
            <Trans>Serial</Trans>
          </b>
          : {device?.serialNumber ?? '-'}
        </Typography>
        <Typography className="md:w-44" variant="body2" style={{ marginRight: 8 }}>
          <b>
            <Trans>SMS</Trans>
          </b>
          : {device?.smsNumber ?? '-'}
        </Typography>
        <Typography
          variant="body2"
          style={{
            marginRight: 8,
            width: mediumScreen ? (onlyCSQData ? 128 : 344) : undefined,
          }}
        >
          <b>
            <Trans>Signal</Trans> - CSQ{onlyCSQData ? '' : ', ECL, RSRP, RSRQ'}
          </b>
          : {device?.csq ?? '-'}
          {onlyCSQData ? '' : `, ${device?.ecl ?? '-'}, ${device?.rsrp ?? '-'}, ${device?.rsrq ?? '-'}`}
        </Typography>
        <Typography className="md:w-36" variant="body2" style={{ marginRight: 8 }}>
          <b>
            <Trans>Battery Level</Trans>
          </b>
          : {device?.battery ?? '-'}
        </Typography>
        <Typography variant="body2" style={{ marginRight: 8 }}>
          <b>
            <Trans>Device Type</Trans>
          </b>
          : {device?.model ?? '-'}
        </Typography>
        <Typography variant="body2" style={{ marginRight: 8 }}>
          <b>
            <Trans>FW</Trans>
          </b>
          : {device?.version ?? '-'}
        </Typography>
        <Typography className="md:w-52" variant="body2" style={{ marginRight: 8 }}>
          <b>
            <Trans>Last Restart</Trans>
          </b>
          : {device?.lastRestart ? formatDate(device?.lastRestart, 'P HH:mm') : '-'}
        </Typography>
      </div>
    </div>
  ) : null;
};
